import { Formik, Form, FieldArray, Field, FormikErrors } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Checkbox,
  Select,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Box,
  useToast,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import { VTODocument, Rock } from '../../types';
import { useUsers } from '../../Users/useUsers';
import { useAuth } from '../../../../contexts/AuthContext';
import { useVTO } from '../useVTO';
import { getQuarterEndDate } from '../vto.util';

interface CreateRocksFromVTOModalProps {
  isOpen: boolean;
  onClose: () => void;
  vto: VTODocument;
  onSuccess: () => void;
}

interface RockSelection {
  rock: string;
  selected: boolean;
  ownerId: string;
}

/**
 * Validation schema for rock selections
 * ownerId is required if selected is true
 */
const validationSchema = Yup.object().shape({
  selections: Yup.array().of(
    Yup.object().shape({
      selected: Yup.boolean(),
      ownerId: Yup.string().when('selected', {
        is: true,
        then: (schema) => schema.required('Owner is required for selected rocks'),
        otherwise: (schema) => schema,
      }),
    })
  ),
});

/**
 * Type guard to check if error is an object with ownerId property
 */
function isRockSelectionError(
  error: string | FormikErrors<RockSelection> | undefined
): error is FormikErrors<RockSelection> {
  return (
    error !== undefined &&
    typeof error === 'object' &&
    error !== null &&
    !Array.isArray(error)
  );
}

/**
 * Create Rocks from VTO Modal Component
 * Rebuilt with Formik/Yup per Directive 5
 * Migrated from Supabase to Firestore
 */
export function CreateRocksFromVTOModal({
  isOpen,
  onClose,
  vto,
  onSuccess,
}: CreateRocksFromVTOModalProps) {
  const { currentUser } = useAuth();
  const { users } = useUsers();
  const { createRocksFromVTO } = useVTO(vto.quarter, vto.year);
  const toast = useToast();

  if ((vto.rocksList || []).length === 0) {
    return null;
  }

  const initialValues = {
    selections: (vto.rocksList || []).map((rock) => ({
      rock,
      selected: true,
      ownerId: currentUser?.uid || '',
    })) as RockSelection[],
  };

  const handleSubmit = async (values: typeof initialValues) => {
    const selectedRocks = values.selections.filter((sel) => sel.selected);

    if (selectedRocks.length === 0) {
      toast({
        title: 'No rocks selected',
        description: 'Please select at least one rock to create',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const invalidRocks = selectedRocks.filter((sel) => !sel.ownerId);
    if (invalidRocks.length > 0) {
      toast({
        title: 'Missing owners',
        description: 'Please assign an owner to all selected rocks',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const quarterEndDate = getQuarterEndDate(vto.quarter, vto.year);

      const rocksToInsert: Array<Omit<Rock, 'id' | 'createdAt' | 'updatedAt'>> = selectedRocks.map((sel) => ({
        rock: sel.rock,
        measurable: 'Defined in V/TO',
        dueDate: quarterEndDate,
        ownerId: sel.ownerId,
        status: 'on-track' as const,
        progress: 0,
        weekNumber: 1,
        notes: `Created from ${vto.quarter} ${vto.year} V/TO`,
        collaborators: [],
        isAchieved: false,
        isDropped: false,
        rockType: 'company' as const,
      }));

      await createRocksFromVTO(rocksToInsert);

      toast({
        title: 'Rocks created successfully',
        description: `Created ${selectedRocks.length} rock(s) from V/TO`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating rocks:', err);
      toast({
        title: 'Error creating rocks',
        description: err.message || 'Failed to create rocks',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Rocks from V/TO</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, isSubmitting, setFieldValue }) => (
            <Form>
              <ModalBody>
                <VStack spacing={4} align="stretch">
                  <Text fontSize="sm" color="gray.600">
                    Select which rocks to create from your {vto.quarter} {vto.year} V/TO and assign owners:
                  </Text>

                  <FieldArray name="selections">
                    {() => (
                      <VStack spacing={3} align="stretch" maxH="96" overflowY="auto">
                        {values.selections.map((selection, index) => (
                          <Box
                            key={index}
                            borderWidth="1px"
                            rounded="lg"
                            p={4}
                            bg={selection.selected ? 'blue.50' : 'gray.50'}
                            borderColor={selection.selected ? 'blue.300' : 'gray.200'}
                          >
                            <HStack spacing={3} align="flex-start">
                              <Checkbox
                                isChecked={selection.selected}
                                onChange={(e) => {
                                  setFieldValue(`selections.${index}.selected`, e.target.checked);
                                  if (!e.target.checked) {
                                    setFieldValue(`selections.${index}.ownerId`, '');
                                  }
                                }}
                                colorScheme="blue"
                                mt={1}
                              />
                              <VStack spacing={2} align="stretch" flex={1}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.900">
                                  {selection.rock}
                                </Text>
                                <FormControl
                                  isInvalid={(() => {
                                    const error = errors.selections?.[index];
                                    return (
                                      error !== undefined &&
                                      isRockSelectionError(error) &&
                                      'ownerId' in error
                                    );
                                  })()}
                                  isDisabled={!selection.selected}
                                >
                                  <FormLabel fontSize="xs" fontWeight="medium" color="gray.600">
                                    Owner:
                                  </FormLabel>
                                  <Select
                                    value={selection.ownerId}
                                    onChange={(e) => {
                                      setFieldValue(`selections.${index}.ownerId`, e.target.value);
                                    }}
                                    isDisabled={!selection.selected}
                                    size="sm"
                                  >
                                    <option value="">Select owner...</option>
                                    {users.map((u) => (
                                      <option key={u.id} value={u.id}>
                                        {u.name}
                                      </option>
                                    ))}
                                  </Select>
                                  <FormErrorMessage>
                                    {(() => {
                                      const error = errors.selections?.[index];
                                      if (
                                        error !== undefined &&
                                        isRockSelectionError(error) &&
                                        'ownerId' in error
                                      ) {
                                        return (error as { ownerId?: string }).ownerId || '';
                                      }
                                      return '';
                                    })()}
                                  </FormErrorMessage>
                                </FormControl>
                              </VStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    )}
                  </FieldArray>

                  <HStack justify="space-between" pt={4} borderTopWidth="1px">
                    <Text fontSize="sm" color="gray.600">
                      {values.selections.filter((s) => s.selected).length} of {values.selections.length} rocks selected
                    </Text>
                  </HStack>
                </VStack>
              </ModalBody>

              <ModalFooter>
                <Button
                  variant="ghost"
                  mr={3}
                  onClick={onClose}
                  isDisabled={isSubmitting}
                  leftIcon={<CloseIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isDisabled={isSubmitting}
                  isLoading={isSubmitting}
                  leftIcon={<CheckIcon />}
                >
                  Create Selected Rocks
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
}

